import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { env } from '@/lib/env';
import { logger } from '@/lib/logger';

export interface FliptProviderProps {
  children?: ReactNode;
}

export type FliptEvaluateBooleanFn = (
  flagKey: string,
  entityId: string,
  context: Record<string, string>,
) => Promise<boolean>;

interface FliptClientContextValue {
  ready: boolean;
  evaluateBoolean: FliptEvaluateBooleanFn | null;
}

const FliptClientContext = createContext<FliptClientContextValue>({
  ready: false,
  evaluateBoolean: null,
});
FliptClientContext.displayName = 'FliptClientContext';

export const useFliptClient = () => useContext(FliptClientContext);

async function httpEvaluateBoolean(
  baseUrl: string,
  namespaceKey: string,
  environment: string | undefined,
  clientToken: string | undefined,
  flagKey: string,
  entityId: string,
  context: Record<string, string>,
): Promise<boolean> {
  const url = `${baseUrl.replace(/\/$/, '')}/evaluate/v1/boolean`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (clientToken) headers.Authorization = `Bearer ${clientToken}`;
  if (environment) headers['X-Flipt-Environment'] = environment;

  const body = JSON.stringify({
    namespaceKey,
    flagKey,
    entityId,
    context: context ?? {},
  });

  const res = await fetch(url, { method: 'POST', headers, body });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Flipt evaluate failed: ${res.status} ${res.statusText} ${text}`,
    );
  }
  const data: { enabled: boolean } = (await res.json()) as { enabled: boolean };
  return !!data.enabled;
}

export const FliptProvider = ({ children }: FliptProviderProps) => {
  const [ready, setReady] = useState<boolean>(false);

  const evaluateBoolean: FliptEvaluateBooleanFn | null = useMemo(() => {
    if (!env.fliptUrl) {
      logger.warn('Flipt URL not configured, feature flags disabled');
      setReady(true);
      return null;
    }
    setReady(true);
    return (flagKey, entityId, context) =>
      httpEvaluateBoolean(
        env.fliptUrl!,
        env.fliptNamespace,
        env.fliptEnvironment,
        env.fliptClientToken,
        flagKey,
        entityId,
        context,
      ).catch((e: unknown) => {
        logger.debug('Flipt HTTP evaluation error', e);
        return false;
      });
  }, []);

  const value = useMemo<FliptClientContextValue>(
    () => ({ ready, evaluateBoolean }),
    [ready, evaluateBoolean],
  );

  return (
    <FliptClientContext.Provider value={value}>
      {children}
    </FliptClientContext.Provider>
  );
};
